import { classList } from '@findigs/helpers/src/helpers/class-list';
import { polymorphic } from '@findigs/helpers/src/helpers/polymorphic';
import type { PolymorphicHTMLProps } from '@findigs/helpers/src/helpers/polymorphic';
import { styles } from './progress-view.module.scss';
import Loading from '../../images/loading.inline.svg';

export type ProgressViewProps = Omit<PolymorphicHTMLProps, 'children'> & {
  loading: boolean;
  loadingText?: string;
};

/**
 * A simple progress/loading indicator (eg. spinner overlay) that can be used inside
 * any non statically-positioned component/element to indicate a loading state.
 *
 * It only becomes part of the DOM when `loading = true`.
 *
 * This component is [polymorphic](/docs/component-system-polymorphic-components--docs).
 */
export const ProgressView = polymorphic(
  { element: 'div', forwardRef: true },
  (
    { className, style, loading, loadingText, ...props }: ProgressViewProps,
    Component,
    ref
  ) => {
    const classes = classList(styles, className);
    return (
      <>
        {loading ? (
          <Component className={classes} style={style} ref={ref}>
            <Loading
              width={48}
              height={48}
              aria-label={loadingText ? undefined : 'Loading'}
              aria-hidden={loadingText ? true : undefined}
              {...props}
            />
            {loadingText ? (
              <p role="status" aria-live="polite" aria-atomic="true">
                {loadingText}
              </p>
            ) : null}
          </Component>
        ) : null}
      </>
    );
  }
);
